import { useCallback, useState } from "react";
import { TOOLBOX_PERSONA_LABELS, type ToolboxPersona } from "@ttc-spectree/toolbox-contract/roles";
import { useProjectPersonas, useSetPersona } from "@/hooks/queries/use-toolbox-access";
import { cn } from "@/lib/utils";

/**
 * The persona picker (epic PI-SPEC-SW-E283): "Working as Product Owner /
 * Developer / QA Tester" on the Formulator start, One item and the Session
 * screen. A person's roles on a project can carry different behaviours (a QA
 * Tester gets tester questions in the Formulator); when they carry more than
 * one, the person picks one per project before a run starts.
 *
 * The choice is stored by SpecTree (`PUT /toolbox/access/:project/persona`),
 * which stamps it on the interview or batch the run creates, so the pages
 * never pass it through the Toolbox bridge. The picker is a segmented radio
 * group rather than a native select, so it renders the same on macOS and
 * Windows.
 */

export interface PersonaChoice {
  /** True when the person's roles on the project map to more than one persona. */
  showPicker: boolean;
  /** The personas to choose from, in contract order. */
  personas: readonly ToolboxPersona[];
  /** The persona picked here, else the one SpecTree has stored, else null. */
  selected: ToolboxPersona | null;
  /** Pick a persona; SpecTree saves it straight away. */
  select: (persona: ToolboxPersona) => void;
  /** Resolves once the picked persona is saved on `project`; rejects when saving failed. Call before starting a run. */
  ensureSaved: (project?: string) => Promise<void>;
  saving: boolean;
  error: string | null;
}

/** The persona choice on `project`: what to show and how to save it before a run. */
export function usePersonaChoice(project: string | null | undefined): PersonaChoice {
  const { personas, persona: stored } = useProjectPersonas(project);
  const setPersona = useSetPersona();
  const [picked, setPicked] = useState<{ project: string; persona: ToolboxPersona } | null>(null);
  const [error, setError] = useState<string | null>(null);

  const key = project ?? "";
  const pickedHere = picked !== null && picked.project.toLowerCase() === key.toLowerCase() ? picked.persona : null;
  const selected = pickedHere && personas.includes(pickedHere) ? pickedHere : (stored ?? personas[0] ?? null);
  const { mutateAsync } = setPersona;

  const save = useCallback(
    async (target: string, persona: ToolboxPersona) => {
      setError(null);
      try {
        await mutateAsync({ project: target, persona });
      } catch (cause) {
        setError("Your choice could not be saved. Try again in a moment.");
        throw cause;
      }
    },
    [mutateAsync]
  );

  const select = useCallback(
    (persona: ToolboxPersona) => {
      if (!project) return;
      setPicked({ project, persona });
      if (persona !== stored) void save(project, persona).catch(() => undefined);
    },
    [project, stored, save]
  );

  const ensureSaved = useCallback(
    async (target?: string) => {
      if (!project || personas.length < 2 || !selected) return;
      if (target !== undefined && target.toLowerCase() !== project.toLowerCase()) return;
      if (selected === stored) return;
      await save(project, selected);
    },
    [project, personas.length, selected, stored, save]
  );

  return {
    showPicker: Boolean(project) && personas.length > 1,
    personas,
    selected,
    select,
    ensureSaved,
    saving: setPersona.isPending,
    error,
  };
}

export interface PersonaPickerProps {
  choice: PersonaChoice;
  className?: string;
}

/** "Working as": shown only when the person's roles on the project carry more than one persona. */
export function PersonaPicker({ choice, className }: PersonaPickerProps) {
  if (!choice.showPicker) return null;
  return (
    <div className={cn("flex flex-wrap items-center gap-2", className)} data-testid="persona-picker">
      <span id="persona-picker-label" className="text-xs font-medium text-muted-foreground">
        Working as
      </span>
      <div
        role="radiogroup"
        aria-labelledby="persona-picker-label"
        className="inline-flex gap-0.5 rounded-[9px] border border-[#1F1F23] bg-[#111113] p-[3px]"
      >
        {choice.personas.map((persona) => {
          const on = persona === choice.selected;
          return (
            <button
              key={persona}
              type="button"
              role="radio"
              aria-checked={on}
              data-testid={`persona-${persona}`}
              disabled={choice.saving}
              className={cn(
                "rounded-md px-[11px] py-1 text-[13px] font-medium transition-colors disabled:opacity-60",
                on ? "bg-[#1F1F23] text-foreground" : "text-muted-foreground hover:text-foreground"
              )}
              onClick={() => {
                choice.select(persona);
              }}
            >
              {TOOLBOX_PERSONA_LABELS[persona]}
            </button>
          );
        })}
      </div>
      {choice.error && (
        <span className="text-[13px] text-destructive" role="alert" data-testid="persona-error">
          {choice.error}
        </span>
      )}
    </div>
  );
}
